import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const STORAGE_KEY = 'built-showcase';

const SHOWCASE_QUERY_KEYS = ['display', 'kiosk', 'showcase'] as const;

export function isTruthyShowcaseParam(value: string | null): boolean {
  if (value === null) return false;
  const normalized = value.trim().toLowerCase();
  return normalized === '' || normalized === '1' || normalized === 'true' || normalized === 'yes';
}

export function readShowcaseFromSearchParams(searchParams: URLSearchParams): boolean {
  return SHOWCASE_QUERY_KEYS.some((key) => isTruthyShowcaseParam(searchParams.get(key)));
}

/**
 * Append car-show kiosk params for QR / stand URLs:
 * page chrome (`display=1`) + Build Explore Car Show Mode.
 */
export function withShowcaseDisplayUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('display', '1');
    parsed.searchParams.set('view', 'explore');
    parsed.searchParams.set('carshow', '1');
    // Land on Build section when the page supports hash navigation
    if (!parsed.hash) parsed.hash = 'build';
    return parsed.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}display=1&view=explore&carshow=1#build`;
  }
}

/**
 * Car-show / iPad kiosk mode. Activated via `?display=1`, `?kiosk=1`, or `?showcase=1`.
 * Persists for the browser tab so in-app navigation keeps the layout.
 */
export function useShowcaseMode(): boolean {
  const [searchParams] = useSearchParams();

  const fromUrl = useMemo(() => readShowcaseFromSearchParams(searchParams), [searchParams]);

  const isShowcaseMode = useMemo(() => {
    if (fromUrl) return true;
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }, [fromUrl]);

  useEffect(() => {
    if (fromUrl) {
      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
    }
  }, [fromUrl]);

  useEffect(() => {
    if (isShowcaseMode) {
      document.documentElement.dataset.showcase = 'true';
    } else {
      delete document.documentElement.dataset.showcase;
    }
    return () => {
      delete document.documentElement.dataset.showcase;
    };
  }, [isShowcaseMode]);

  return isShowcaseMode;
}
